import React, { useCallback } from 'react';
import { Card, CardContent, CardHeader } from './ui/card';
import { Input } from './ui/input';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from './ui/select';
import { Switch } from './ui/switch';
import { Label } from './ui/label';
import { Button } from './ui/button';
import { AdditionalDeduction } from '../types/AdditionalDeductions';
import { cn } from "@/lib/utils";

interface AdditionalDeductionsProps {
  enabled: boolean;
  deductions: AdditionalDeduction[];
  personsToggle: boolean;
  deductionsPerson1?: AdditionalDeduction[];
  deductionsPerson2?: AdditionalDeduction[];
  onEnabledChange: (enabled: boolean) => void;
  onDeductionsChange: (deductions: AdditionalDeduction[]) => void;
  onDeductionsPerson1Change?: (deductions: AdditionalDeduction[]) => void;
  onDeductionsPerson2Change?: (deductions: AdditionalDeduction[]) => void;
}

const AdditionalDeductions: React.FC<AdditionalDeductionsProps> = ({
  enabled,
  deductions,
  personsToggle,
  deductionsPerson1 = [],
  deductionsPerson2 = [],
  onEnabledChange,
  onDeductionsChange,
  onDeductionsPerson1Change,
  onDeductionsPerson2Change,
}) => {
  const generateId = () => Math.random().toString(36).substr(2, 9);

  const addDeduction = useCallback((person?: 1 | 2) => {
    const newDeduction: AdditionalDeduction = {
      id: generateId(),
      title: '',
      type: 'percentage',
      amount: '',
      reducesTaxableIncome: false
    };

    if (personsToggle) {
      if (person === 1) {
        onDeductionsPerson1Change?.([...deductionsPerson1, newDeduction]);
      } else if (person === 2) {
        onDeductionsPerson2Change?.([...deductionsPerson2, newDeduction]);
      }
    } else {
      onDeductionsChange([...deductions, newDeduction]);
    }
  }, [personsToggle, deductions, deductionsPerson1, deductionsPerson2, onDeductionsChange, onDeductionsPerson1Change, onDeductionsPerson2Change]);

  const updateDeduction = useCallback((id: string, field: keyof AdditionalDeduction, value: string | boolean, person?: 1 | 2) => {
    const updateList = (currentDeductions: AdditionalDeduction[]) =>
      currentDeductions.map(d => d.id === id ? { ...d, [field]: value } : d);

    if (personsToggle) {
      if (person === 1) {
        onDeductionsPerson1Change?.(updateList(deductionsPerson1));
      } else if (person === 2) {
        onDeductionsPerson2Change?.(updateList(deductionsPerson2));
      }
    } else {
      onDeductionsChange(updateList(deductions));
    }
  }, [personsToggle, deductions, deductionsPerson1, deductionsPerson2, onDeductionsChange, onDeductionsPerson1Change, onDeductionsPerson2Change]);

  const removeDeduction = useCallback((id: string, person?: 1 | 2) => {
    const removeFromList = (currentDeductions: AdditionalDeduction[]) =>
      currentDeductions.filter(d => d.id !== id);

    if (personsToggle) {
      if (person === 1) {
        onDeductionsPerson1Change?.(removeFromList(deductionsPerson1));
      } else if (person === 2) {
        onDeductionsPerson2Change?.(removeFromList(deductionsPerson2));
      }
    } else {
      onDeductionsChange(removeFromList(deductions));
    }
  }, [personsToggle, deductions, deductionsPerson1, deductionsPerson2, onDeductionsChange, onDeductionsPerson1Change, onDeductionsPerson2Change]);

  const renderDeductionList = (deductionList: AdditionalDeduction[], person?: 1 | 2, title?: string) => (
    <div className="space-y-4">
      {title && (
        <h4 className="text-sm font-semibold text-foreground flex items-center gap-2">
          {title}
          <div className="h-px bg-border flex-1"></div>
        </h4>
      )}
      
      {deductionList.length === 0 ? (
        <div className="text-sm text-muted-foreground italic p-4 border border-dashed border-border rounded-md text-center">
          No additional deductions added yet
        </div>
      ) : (
        deductionList.map((deduction) => (
          <div key={deduction.id} className="p-4 border border-border rounded-md bg-muted/20 space-y-4">
            {/* Title field - full width */}
            <div>
              <Label className="text-xs font-medium text-muted-foreground mb-1 block">Title</Label>
              <Input
                placeholder="e.g., Retirement Savings, Health Insurance"
                value={deduction.title}
                onChange={(e) => updateDeduction(deduction.id, 'title', e.target.value, person)}
                className="text-sm w-full"
              />
            </div>
            
            {/* Type and Amount in a grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="text-xs font-medium text-muted-foreground mb-1 block">Type</Label>
                <Select value={deduction.type} onValueChange={(value) => updateDeduction(deduction.id, 'type', value as 'fixed' | 'percentage', person)}>
                  <SelectTrigger className="text-sm w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="percentage">Percentage (%)</SelectItem>
                    <SelectItem value="fixed">Fixed Amount ($)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label className="text-xs font-medium text-muted-foreground mb-1 block">Amount</Label>
                <div className="relative">
                  <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-muted-foreground text-sm">
                    {deduction.type === 'fixed' ? '$' : ''}
                  </span>
                  <Input
                    type="number"
                    placeholder={deduction.type === 'fixed' ? '2000' : '10'}
                    value={deduction.amount}
                    onChange={(e) => updateDeduction(deduction.id, 'amount', e.target.value, person)}
                    className={cn("text-sm w-full", deduction.type === 'fixed' ? 'pl-8 pr-8' : 'pl-3 pr-8')}
                  />
                  <span className="absolute inset-y-0 right-0 flex items-center pr-3 text-muted-foreground text-sm">
                    {deduction.type === 'percentage' ? '%' : ''}
                  </span>
                </div>
              </div>
            </div>
            
            {/* Tax reduction toggle and remove button */}
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <Switch
                  id={`tax-reduction-${deduction.id}`}
                  checked={deduction.reducesTaxableIncome}
                  onCheckedChange={(checked) => updateDeduction(deduction.id, 'reducesTaxableIncome', checked, person)}
                />
                <Label htmlFor={`tax-reduction-${deduction.id}`} className="text-xs text-muted-foreground">
                  Reduces taxable income
                </Label>
              </div>
              
              <Button
                variant="outline"
                size="sm"
                onClick={() => removeDeduction(deduction.id, person)}
                className="text-sm text-destructive hover:bg-destructive hover:text-destructive-foreground"
              >
                Remove
              </Button>
            </div>
          </div>
        ))
      )}
      
      <Button
        variant="outline"
        size="sm"
        onClick={() => addDeduction(person)}
        className="w-full"
      >
        Add Deduction
      </Button>
    </div>
  );

  if (!enabled) {
    return (
      <div className="p-4 rounded-lg bg-muted/30 border border-border">
        <div className="flex items-center justify-between">
          <div>
            <Label htmlFor="additionalDeductions" className="text-sm font-medium text-foreground">
              Additional Deductions
            </Label>
            <p className="text-xs text-muted-foreground mt-1">
              Add paycheck deductions like retirement contributions, insurance, etc.
            </p>
          </div>
          <Switch
            id="additionalDeductions"
            checked={enabled}
            onCheckedChange={onEnabledChange}
          />
        </div>
      </div>
    );
  }

  return (
    <Card className="shadow-sm">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-foreground">Additional Deductions</h3>
            <p className="text-sm text-muted-foreground">
              Add paycheck deductions like insurance, retirement savings, etc.
            </p>
          </div>
          <Switch
            checked={enabled}
            onCheckedChange={onEnabledChange}
          />
        </div>
      </CardHeader>
      
      <CardContent className="space-y-6">
        {!personsToggle ? (
          renderDeductionList(deductions)
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div>
              {renderDeductionList(deductionsPerson1, 1, "Primary Income")}
            </div>
            <div className="border-l-2 border-border pl-4">
              {renderDeductionList(deductionsPerson2, 2, "Secondary Income")}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default AdditionalDeductions;